import { useEffect, useMemo, useState } from "react";
import { Award, Eye, Layers } from "lucide-react";

import { API_EMPLOYEE_CERTIFICATION } from "../../../../config/Endpoints.js";
import { Card } from "../../../components/shared/card/card.tsx";
import { EmptyStateMessage } from "../../../components/shared/empty-state-message/empty-state-message.tsx";
import {
  Badge,
  Button,
  ResponsiveDataTable,
  Spinner,
  type ResponsiveColumn,
} from "../../../components/ui/index.ts";
import { CertificationDocumentViewer } from "../certifications/certification-document-viewer.tsx";
import { useCertificationDocument } from "../certifications/use-certification-document.ts";
import { useTechStackList } from "../../../pages/tech-stack-list/use-tech-stack-list.ts";
import type {
  EmployeeCertification,
  EmployeeCertificationRow,
} from "../../../pages/tech-stack-list/certifications/certification-types.ts";
import { getApi } from "../../../utils/api.ts";
import { accessToken } from "../../../utils/auth.ts";
import { showError } from "../../../utils/toast.ts";
import { formatToLongDate } from "../../../utils/helpers.ts";
import styles from "./employee-profile-skills.module.css";
import {formatEnumLabel} from "../../../utils/helpers.ts";
type EmployeeSkillsViewProps = {
  employeeId: number | string;
  embedded?: boolean;
};

export function EmployeeTechStackView({ employeeId, embedded }: EmployeeSkillsViewProps) {
  const id = String(employeeId);
  const filters = useMemo(
    () => ({ employeeId: id, categoryId: "", techStackId: "", techStackAssigned: "", technicalStatus: "", location: "" }),
    [id],
  );
  const list = useTechStackList(filters, 1, 10);

  useEffect(() => {
    if (list.error) showError(list.error);
  }, [list.error]);

  const skills = list.rows.find((row) => String(row.employeeId) === id)?.techStacks ?? [];

  const content = <>
      {!embedded ? <div className={styles.heading}>
        <span className={styles.icon}><Layers size={21} aria-hidden /></span>
        <div><h3>Tech Stack</h3><p>Technologies and tools assigned to this employee.</p></div>
      </div> : null}
      {list.loading ? <div className={styles.state}><Spinner label="Loading tech stack" /></div>
        : skills.length === 0 ? <EmptyStateMessage message="No tech stack assigned." />
          : <ul className={styles.skills}>{skills.map((skill) => (
            <li className={styles.skill} key={skill.techStackId}>
              <span className={styles.dot} aria-hidden />
              <span>{skill.techStack}</span>
              {skill.primarySkill ? <Badge variant="success">Primary</Badge> : null}
            </li>
          ))}</ul>}
    </>;

  return embedded ? <div className={styles.embedded}>{content}</div> : (
    <Card className={styles.card}>{content}</Card>
  );
}

type CertificationRow = EmployeeCertification & { id: string } & Record<string, unknown>;


export function EmployeeCertificationsView({ employeeId, embedded }: EmployeeSkillsViewProps) {
  const [rows, setRows] = useState<CertificationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const documentViewer = useCertificationDocument(accessToken());

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setFailed(false);
    getApi<EmployeeCertificationRow | { data: EmployeeCertificationRow }>(
      `${API_EMPLOYEE_CERTIFICATION}/${encodeURIComponent(String(employeeId))}`,
      accessToken(),
      "json",
      controller.signal,
    ).then(({ data }) => {
      if (controller.signal.aborted) return;
      const payload = (
        data && typeof data === "object" && "data" in data ? data.data : data
      ) as EmployeeCertificationRow | null;
      setRows((payload?.certifications ?? []).map((item, index) => ({
        ...item,
        id: String(item.employeeCertificationId ?? `${item.certificationId}-${index}`),
      })));
    }).catch(() => {
      if (!controller.signal.aborted) {
        setFailed(true);
        setRows([]);
        showError("Failed to load employee certifications.");
      }
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [employeeId]);

  const columns = useMemo<ResponsiveColumn<CertificationRow>[]>(() => [
    { id: "certification", header: "Certification", accessor: "certification" },
    { id: "category", header: "Category", render: (row) => formatEnumLabel(row.category) },
    { id: "certificateNumber", header: "Certificate No.", accessor: "certificateNumber" },
    { id: "issueDate", header: "Issued On", render: (row) => formatToLongDate(row.issueDate) },
    { id: "expiryDate", header: "Expiry On", render: (row) => formatToLongDate(row.expiryDate) },
    { id: "verified", header: "Status", render: (row) => (
      <Badge variant={row.verified ? "success" : "warning"}>
        {row.verified ? "Verified" : "Not Verified"}
      </Badge>
    ) },
    { id: "document", header: "Document", render: (row) => row.documentPath ? (
      <Button
        size="sm"
        variant="accent-ghost"
        startIcon={<Eye size={15} aria-hidden />}
        disabled={Boolean(documentViewer.loadingPath)}
        onClick={() => void documentViewer.view(row.documentPath!)}
      >
        {documentViewer.loadingPath === row.documentPath ? "Loading..." : "View"}
      </Button>
    ) : "-" },
  ], [documentViewer]);

  const content = <>
      {!embedded ? <div className={styles.heading}>
        <span className={styles.icon}><Award size={21} aria-hidden /></span>
        <div><h3>Certifications</h3><p>Professional certifications uploaded by this employee.</p></div>
      </div> : null}
      {loading ? <div className={styles.state}><Spinner label="Loading certifications" /></div>
        : failed ? <EmptyStateMessage message="Unable to load certifications." />
          : <ResponsiveDataTable<CertificationRow>
            columns={columns}
            data={rows}
            aria-label="Employee certifications"
            emptyState={<EmptyStateMessage message="No certifications uploaded." />}
          />}
      <CertificationDocumentViewer document={documentViewer.document} onClose={documentViewer.close} />
    </>;

  return embedded ? <div className={styles.embedded}>{content}</div> : (
    <Card className={styles.card}>{content}</Card>
  );
}
